import { Component, OnDestroy } from '@angular/core';
import Highcharts from 'highcharts';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { getUniqueValues } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { InputDateFormatPipe } from 'src/app/modules/core/pipes/input-date-format.pipe';
import { TimePresentationPipe } from 'src/app/modules/core/pipes/time-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { IResultLoopsByModelTruck } from '../../../interfaces/production-model-truck';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';

@Component({
  selector: 'app-production-hour-by-model-truck',
  templateUrl: './production-hour-by-model-truck.component.html',
  styleUrls: ['./production-hour-by-model-truck.component.scss'],
})
export class ProductionHourByModelTruckComponent extends DashboardWidgetChartComponent implements OnDestroy {
  public productionByHourModel: IResultLoopsByModelTruck[];
  public dataSeries: any;
  public displayMessage = false;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    protected languageService: LanguageService,
    protected timePipe: TimePresentationPipe,
    private formatDatePipe: InputDateFormatPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  /**
   * Init RequestParams
   */
  private initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
    };
  }

  /**
   * DrawView Chart
   */
  public drawView(chartOptions?: IResultLoopsByModelTruck[]): void {
    this.hasData = !!chartOptions.length;
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value;

    this.displayMessage = true;
    if (chartOptions.length) {
      this.productionByHourModel = chartOptions;

      const timePeriod = this.productionByHourModel.map((m) => {
        return { periodStartTime: m.periodStartTime };
      });

      const model = this.productionByHourModel.map((m) => {
        return { modelTruck: m.modelTruck };
      });

      const categoriesTime = getUniqueValues(timePeriod, 'periodStartTime').map((m) => this.timePipe.transform(m.periodStartTime));
      const modelTrucks = getUniqueValues(model, 'modelTruck').map((m) => m.modelTruck);

      const categories = categoriesTime;
      const modelTruck = modelTrucks;

      const inputDate = String(this.parameterService.parameters.item('date').value);

      const series = [];
      for (const truck of modelTruck) {
        const color = `${this.productionByHourModel.find((f) => f.modelTruck === truck).colour}80`;
        const serie = {
          name: truck,
          type: 'line',
          data: this.productionByHourModel.filter((f) => f.modelTruck === truck).map((val) => val.loops),
          color,
          borderWidth: 2,
          borderColor: color.slice(0, -2),
          states: {
            hover: {
              color,
            },
          },
        };
        series.push(serie);
      }

      const configChart = {
        chart: {
          plotBorderWidth: null,
          plotShadow: false,
          backgroundColor: '#F8F8F8',
          style: {
            fontFamily: 'Open Sans',
          },
        },
        credits: {
          enabled: false,
        },
        subtitle: {
          text: `${this.languageService.message('company')}: ${user.company.name},
                ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
                ${this.languageService.message('date')}: ${this.formatDatePipe.transform(inputDate)},
                ${this.languageService.message('shift_')}: ${shift.name}`,
        },
        title: {
          text: '',
          style: {
            fontSize: 12,
            fontWeight: '600',
            fontFamily: 'Open Sans',
            color: '#433E3E',
          },
        },
        xAxis: {
          labels: {
            style: {
              color: '#707070',
            },
          },
          categories,
          title: {
            text: this.languageService.message('hours').toUpperCase(),
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '600',
            },
          },
        },
        yAxis: {
          labels: {
            style: {
              color: '#707070',
            },
          },
          title: {
            text: this.languageService.message('quantity-loops').toUpperCase(),
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '600',
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgb(51, 51, 51, 1)',
          borderColor: '#333333',
          borderRadius: 2,
          style: {
            color: '#FFFFFF',
          },
          shared: true,
          useHTML: true,
          formatter(): string {
            let HTML = `<div style="width: 180px;
            font-size: 10px;
            color: #FFFFFF;">
            <span style="font-weight: 400;">${this.points[0].x}</span> <br>`;
            for (const point of this.points) {
              HTML += `<div style="background-color: ${point.color};
                            width: 30px;
                            height: 12px;
                            border: 1px solid ${point.color};
                            float: left;
                            margin-top: 2px">
                        </div> &nbsp;
                      <span style="font-weight: 400;">vueltas: ${point.y}</span>
                      <br>
                    `;
            }
            HTML += `</div>`;
            return HTML;
          },
        },
        legend: {
          squareSymbol: false,
          align: 'center',
          verticalAlign: 'top',
          symbolHeight: 0.001,
          symbolWidth: 0.001,
          symbolRadius: 0.001,
          margin: 30,
          reversed: true,
          itemStyle: {
            fontSize: 12,
            fontWeight: '600',
            fontFamily: 'Open Sans',
            color: '#433E3E',
          },
          itemCheckboxStyle: {
            color: '#ffffff',
          },
          useHTML: true,
          labelFormatter(): string {
            return `
            <div style=" height: 20px; display: flex;" (click)="null">
              <svg width="36" height="18" style=" float: left;">
                <rect width="30" height="12" x="2" y="2"
                style="fill: ${this.color}; stroke-width: 2px !important;
                stroke: ${this.color.slice(0, -2)} !important;"/>
              </svg>

              <span style="font-size: 12px; font-family: Open Sans; font-weight: 500;
              color: #433E3E; float: left;">${this.name}</span>
            </div>`;
          },
        },
        plotOptions: {
          series: {
            // stacking: 'normal',
            events: {
              legendItemClick(): boolean {
                return false;
              },
            },
          },
        },
        series,
      };

      this.chart = Highcharts.chart(this.chartTarget.nativeElement, configChart as any, (chart) => {
        if (!this.hasData) {
          const newX = chart.plotWidth / 3 + chart.plotLeft;
          const newY = chart.plotHeight / 2 + chart.plotTop;
          chart.renderer
            .text('La solicitud no arrojó resultados', newX, newY)
            .css({
              color: '#4572A7',
              fontSize: '24px',
            })
            .add();
        }
      });
      window.dispatchEvent(new Event('resize'));
    } else {
      this.displayMessage = false;
    }
  }
  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
